import ErrorSnackbar from 'frontend/components/ErrorSnackbar';
import extractFlightData from 'frontend/helpers/extractFlightPricingData';
import { getCacheStatus } from 'frontend/helpers/getCacheStatus';
import { prepareEmailData } from 'frontend/helpers/prepareEmailData';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PackagesApi from '../api/paquetes';
import { CACHE_DEFAULTS } from '../constants';
import { safeSessionStorage, useSessionStorage } from 'frontend/helpers/ssr-helper';

const FlightDetailsContext = createContext();

export const FlightProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const agency = useSelector((state) => state.applicationState.templateData.header);
  const principalOffice = useSelector(
    (state) => state.applicationState.templateData.principalOffice,
  );
  const tokenMarcaBlanca = useSelector((state) => state.applicationState.token);
  const { emailContacto: emailAgency, empresa: nameAgency, telefonoContacto: agencyPhone } = agency;

  const [flightSelections, setFlightSelections] = useState(() => {
    try {
      const saved = safeSessionStorage.getItem('flightSelections');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      return {};
    }
  });

  const [isOnlyOneWay, setIsOnlyOneWay] = useState(() => {
    try {
      const saved = safeSessionStorage.getItem('isOnlyOneWay');
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      return false;
    }
  });

  const [selectedFare, setSelectedFare] = useState(() => {
    try {
      const saved = safeSessionStorage.getItem('selectedFare');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  });

  const [flightInfo, setFlightInfo] = useState(() => {
    try {
      const saved = safeSessionStorage.getItem('flightInfo');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  });

  const [expCacheDate, setExpCacheDate] = useState(() => {
    try {
      const saved = safeSessionStorage.getItem('expCacheDate');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleFlightDetails = async (cardId) => {
    const selectedFlights = flightSelections[cardId];
    if (
      (isOnlyOneWay && !selectedFlights?.ida) ||
      (!isOnlyOneWay && (!selectedFlights?.vuelta || !selectedFlights?.ida))
    )
      return;

    const cacheValue = getCacheStatus();

    try {
      const data = {
        token: selectedFlights.ida.token,
        solutionID0: selectedFlights.ida.solutionId,
        journey00: selectedFlights.ida.journey,
        journey01: isOnlyOneWay ? selectedFlights.ida.journey : selectedFlights.vuelta.journey,
        cache: cacheValue,
      };

      const response = await PackagesApi.getLlegoFlightDetails(data);
      return { success: true, data: response.data };
    } catch (error) {
      showError(error?.message);
      if (error?.response?.status === 400) {
        return { success: false, error };
      }
      throw error;
    }
  };

  const lastCardId = useMemo(() => {
    return Object.keys(flightSelections)[Object.keys(flightSelections).length - 1] || null;
  }, [flightSelections]);

  const handlePricingAndStandalone = async () => {
    if (!lastCardId) return;
    const selectedFlights = flightSelections[lastCardId];
    if (!selectedFlights?.ida || (!isOnlyOneWay && !selectedFlights?.vuelta)) return;

    const cacheValue = getCacheStatus();

    try {
      const data = {
        token: selectedFlights.ida.token,
        solutionID0: selectedFare.id,
        journey00: selectedFare.journeys_ids[0],
        journey01: isOnlyOneWay ? selectedFare.journeys_ids[0] : selectedFare.journeys_ids[1],
        cache: cacheValue,
      };

      setFlightInfo((prevInfo) => {
        const updatedInfo = { ...(prevInfo || {}), ...data };
        return updatedInfo;
      });

      const [pricingResponse, standaloneResponse] = await Promise.all([
        PackagesApi.getLlegoPricing(data),
        PackagesApi.getLlegoStandalone(data),
      ]);

      return {
        success: true,
        pricing: extractFlightData(pricingResponse),
        standalone: standaloneResponse,
      };
    } catch (error) {
      showError(error?.message);
      if (error?.response?.status === 400) {
        return { success: false, error };
      }
      throw error;
    }
  };

  const createBooking = async (bookingModel) => {
    setIsLoading(true);
    setErrorState(null);

    try {
      const token = await PackagesApi.getToken();
      
      const bookingModelWithAgency = {
        ...bookingModel,
        nameAgency,
        emailAgency,
        agencyPhone
      };
      const response = await PackagesApi.createBooking(bookingModelWithAgency, token);

      if (!response?.success) {
        if (!!response?.booking?.id) {
          return {
            success: true,
            data: response,
          };
        }

        return {
          success: false,
          errors: response?.errors,
        };
      }

      return {
        success: true,
        data: response,
      };
    } catch (err) {
      console.error('Error en createBooking:', err);
      setErrorState(err || 'No se pudo crear la reserva, intente nuevamente o contacte a soporte');
      return {
        success: false,
        error: err || 'No se pudo crear la reserva, intente nuevamente o contacte a soporte',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const sendEmailInfo = async (
    contactInfo,
    bookingData = null,
    pricingData = null,
    apiBookingResponse = null,
    extraBaggage = {},
  ) => {
    setIsLoading(true);
    setErrorState(null);
    const emailData = {
      emailAgency,
      email: contactInfo.email,
      nameAgency,
      agencyAddress: principalOffice?.direccion,
      agencyPhone,
      name: contactInfo?.name,
      surnames: contactInfo?.surnames,
      phone: contactInfo?.telefono1?.numero,
    };

    const finalEmailData = prepareEmailData(
      emailData,
      bookingData,
      pricingData,
      apiBookingResponse,
      extraBaggage,
    );

    try {
      const result = await PackagesApi.sendMail(finalEmailData, tokenMarcaBlanca);
      return {
        success: true,
        data: result,
      };
    } catch (err) {
      console.error('Error en sendEmail:', err);
      setErrorState(err || 'No se pudo enviar el mail, intente nuevamente o contacte a soporte');
      return {
        success: false,
        error: err || 'No se pudo enviar el mail, intente nuevamente o contacte a soporte',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const setOneWayFlight = useCallback((value) => {
    setIsOnlyOneWay(value);
    try {
      safeSessionStorage.setItem('isOnlyOneWay', JSON.stringify(value));
    } catch (error) {
      console.warn('Error saving isOnlyOneWay to sessionStorage:', error);
    }
  }, []);

  const selectFlight = useCallback((cardId, flight, type) => {
    setFlightSelections((prev) => {
      const newSelections = {
        ...prev,
        [cardId]: {
          ...prev[cardId],
          [type]: flight,
        },
      };
      try {
        safeSessionStorage.setItem('flightSelections', JSON.stringify(newSelections));
      } catch (error) {
        console.warn('Error saving flight selections to sessionStorage:', error);
      }
      return newSelections;
    });
  }, []);

  const selectFare = useCallback((fareData) => {
    setSelectedFare(fareData);
    try {
      safeSessionStorage.setItem('selectedFare', JSON.stringify(fareData));
    } catch (error) {
      console.warn('Error saving fare to sessionStorage:', error);
    }
  }, []);

  const setFlightInformation = useCallback((info) => {
    setFlightInfo((prevInfo) => {
      const updatedInfo = { ...(prevInfo || {}), ...info };

      // Only update if the new info is different from the current info
      if (JSON.stringify(updatedInfo) !== JSON.stringify(prevInfo)) {
        return updatedInfo;
      }
      return prevInfo;
    });
  }, []);

  const cleanSessionStorage = useCallback(() => {
    try {
      safeSessionStorage.removeItem('flightSelections');
      safeSessionStorage.removeItem('isOnlyOneWay');
      safeSessionStorage.removeItem('selectedFare');
      safeSessionStorage.removeItem('flightInfo');
      setFlightSelections({});
      setIsOnlyOneWay(false);
      setSelectedFare(null);
      setFlightInfo(null);
    } catch (error) {
      console.warn('Error cleaning sessionStorage:', error);
    }
  }, []);

  const handleCacheExpiration = useCallback((apiExpirationDate) => {
    const expirationDate =
      apiExpirationDate || new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME);
    setExpCacheDate(expirationDate);
  }, []);

  useEffect(() => {
    if (expCacheDate) {
      try {
        safeSessionStorage.setItem('expCacheDate', JSON.stringify(expCacheDate));
      } catch (error) {
        console.warn('Error saving expCacheDate to sessionStorage:', error);
      }
    }
  }, [expCacheDate]);

  useEffect(() => {
    if (flightInfo) {
      try {
        const savedFlightInfo = safeSessionStorage.getItem('flightInfo');
        const parsedSavedFlightInfo = savedFlightInfo ? JSON.parse(savedFlightInfo) : null;

        // Solo actualizamos si el flightInfo tiene información que no está en el sessionStorage
        if (parsedSavedFlightInfo) {
          const updatedInfo = {
            ...parsedSavedFlightInfo,
            ...flightInfo,
            // Preservamos los códigos de país del sessionStorage
            originCountryCode: parsedSavedFlightInfo.originCountryCode,
            destinationCountryCode: parsedSavedFlightInfo.destinationCountryCode,
            origin: {
              ...parsedSavedFlightInfo.origin,
              ...flightInfo.origin,
              countryCode: parsedSavedFlightInfo.origin?.countryCode
            },
            destination: {
              ...parsedSavedFlightInfo.destination,
              ...flightInfo.destination,
              countryCode: parsedSavedFlightInfo.destination?.countryCode
            }
          };

          if (JSON.stringify(updatedInfo) !== JSON.stringify(parsedSavedFlightInfo)) {
            safeSessionStorage.setItem('flightInfo', JSON.stringify(updatedInfo));
          }
        } else {
          // Si no hay información en sessionStorage, guardamos el flightInfo completo
          safeSessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
        }
      } catch (error) {
        console.warn('Error saving flight info to sessionStorage:', error);
      }
    }
  }, [flightInfo]);

  const value = useMemo(
    () => ({
      flightSelections,
      selectFlight,
      handleFlightDetails,
      handlePricingAndStandalone,
      selectedFare,
      selectFare,
      flightInfo,
      setFlightInformation,
      isOnlyOneWay,
      setOneWayFlight,
      createBooking,
      sendEmailInfo,
      isLoading,
      error: errorState,
      cleanSessionStorage,
      expCacheDate,
      setExpCacheDate,
      handleCacheExpiration,
    }),
    [
      flightSelections,
      selectFlight,
      handleFlightDetails,
      handlePricingAndStandalone,
      selectedFare,
      selectFare,
      flightInfo,
      setFlightInformation,
      isOnlyOneWay,
      setOneWayFlight,
      createBooking,
      sendEmailInfo,
      isLoading,
      errorState,
      cleanSessionStorage,
      expCacheDate,
      handleCacheExpiration,
    ],
  );

  return (
    <FlightDetailsContext.Provider value={value}>
      {children}
      <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
    </FlightDetailsContext.Provider>
  );
};

export const useFlightContext = () => {
  const context = useContext(FlightDetailsContext);
  if (!context) throw new Error('useFlightContext must be used within FlightProvider');
  return context;
};
